import Reveal from "@/components/Reveal";

/**
 * Day-by-day production timeline — five working days from intake to delivery,
 * plus the +3 day competitor intelligence note for GO brands.
 */
const steps = [
  {
    day: "Day 1 · AM",
    t: "Intake reviewed · Category confirmed",
    d: "Founder intake is reviewed, category mapped to the relevant Amazon EU browse nodes, and a confirmation email issued with the assigned Bearing reference.",
  },
  {
    day: "Day 1 · PM",
    t: "Competitor mapping begins",
    d: "Top 20 ASINs in your category are profiled — price architecture, review velocity, content quality, ranking history — using Keepa and Helium 10.",
  },
  {
    day: "Day 2",
    t: "Keyword gap analysis",
    d: "Cerebro is run against the five strongest competitors. Undefended keyword clusters identified and translated for the German locale.",
  },
  {
    day: "Day 3",
    t: "Brand readiness + financial model",
    d: "Operator-led assessment across seven readiness sub-dimensions. Conservative, base and optimistic Y1 financial models built — net to brand after every fee.",
  },
  {
    day: "Day 4",
    t: "Risk register · Scoring finalised",
    d: "All six dimension scores finalised, composite calculated, risk register compiled, and the Bearing document drafted for operator review.",
  },
  {
    day: "Day 5 · AM",
    t: "Bearing delivered",
    d: "Three-page PDF delivered to the founder. Verdict, dimension scores, EU market opportunity, critical insight, projections, risk register, and recommended next step.",
  },
  {
    day: "Day 8 · +3 days",
    t: "Competitor intelligence update",
    d: "Three days after delivery, GO brands receive one additional note — what the top three competitors moved on while your Bearing was being prepared. Standard and Premium tiers only.",
    accent: true,
  },
];

export default function BearingTimeline() {
  return (
    <section className="bg-navy text-paper" data-testid="bearing-timeline">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 sm:py-20 lg:py-24">
        <Reveal>
          <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-paper/55">
            / Day by day
          </p>
          <h2 className="mt-8 sm:mt-10 font-serif-i text-[36px] sm:text-[44px] lg:text-[56px] leading-[1.05] tracking-tight max-w-3xl">
            From intake to Bearing — <span className="text-coral">five working days.</span>
          </h2>
          <p className="mt-8 max-w-xl text-paper/65 leading-[1.7] text-[16px] sm:text-lg">
            One operator. Methodical sequence. Every assumption written out so the founder can
            audit the work — not just the conclusion.
          </p>
        </Reveal>

        <ol className="mt-10 sm:mt-14 border-t border-paper/15">
          {steps.map((s, i) => (
            <Reveal key={i} delay={i * 0.04}>
              <li
                data-testid={`timeline-step-${i}`}
                className="grid grid-cols-12 gap-6 sm:gap-10 py-8 sm:py-10 border-b border-paper/15"
              >
                <div className="col-span-12 sm:col-span-3">
                  <span
                    className={`font-mono text-[11px] tracking-[0.22em] uppercase ${s.accent ? "text-coral" : "text-paper/55"}`}
                  >
                    {s.day}
                  </span>
                </div>
                <div className="col-span-12 sm:col-span-9">
                  <h3
                    className={`font-serif-i text-[22px] sm:text-[26px] lg:text-[30px] leading-[1.15] ${s.accent ? "text-coral" : "text-paper"}`}
                  >
                    {s.t}
                  </h3>
                  <p className="mt-4 sm:mt-5 text-paper/65 leading-[1.7] text-[14px] sm:text-[15px] max-w-2xl">
                    {s.d}
                  </p>
                </div>
              </li>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
}
